/**
 * Browser Notifications + Web Push Subscription Management
 *
 * Features:
 * - Desktop notifications for new messages (tab not focused)
 * - Document title badge with unread count
 * - Service Worker registration for Web Push
 * - Push subscription management (subscribe/unsubscribe)
 */

import { api } from "./api.js";

const DEFAULT_TITLE = "KollegePlus";
let permissionGranted = false;
let swRegistration: ServiceWorkerRegistration | null = null;

// ============================================================
// Service Worker Registration
// ============================================================

/** Register the service worker and set up push. Call once on app init. */
export async function initPushNotifications(): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    console.warn("Service Worker not supported");
    return;
  }

  try {
    swRegistration = await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
    });
    console.debug("Service Worker registered:", swRegistration.scope);

    // Listen for messages from SW (notification clicks, subscription changes)
    navigator.serviceWorker.addEventListener("message", handleSwMessage);

    // Wait for SW to be active
    if (swRegistration.active) {
      // Already active
    } else {
      await new Promise<void>((resolve) => {
        const sw = swRegistration!.installing || swRegistration!.waiting;
        if (!sw) return resolve();
        sw.addEventListener("statechange", () => {
          if (sw.state === "activated") resolve();
        });
      });
    }
  } catch (err) {
    console.error("Service Worker registration failed:", err);
  }
}

/** Handle messages from the Service Worker */
function handleSwMessage(event: MessageEvent) {
  const { data } = event;

  switch (data?.type) {
    case "NOTIFICATION_CLICK": {
      // Navigate to the channel that was clicked in the notification
      if (data.channelId) {
        // Dispatch custom event that ChatPage can listen to
        window.dispatchEvent(
          new CustomEvent("push-navigate", {
            detail: { channelId: data.channelId, messageId: data.messageId },
          }),
        );
      }
      break;
    }
    case "PUSH_SUBSCRIPTION_CHANGED": {
      // Re-send subscription to server
      if (data.subscription) {
        sendSubscriptionToServer(data.subscription).catch(console.error);
      }
      break;
    }
  }
}

// ============================================================
// Push Subscription
// ============================================================

/** Subscribe to Web Push notifications. Returns true if successful. */
export async function subscribeToPush(): Promise<boolean> {
  if (!swRegistration) {
    console.warn("No service worker registration available");
    return false;
  }

  try {
    // Request notification permission
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.info("Notification permission denied");
      return false;
    }
    permissionGranted = true;

    // Get VAPID public key from server
    const { vapidPublicKey } = await api.get<{ vapidPublicKey: string }>(
      "/users/me/vapid-key",
    );

    // Convert VAPID key to Uint8Array
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

    // Check for existing subscription
    let subscription = await swRegistration.pushManager.getSubscription();

    if (subscription) {
      // Already subscribed — send to server in case it's a new login
      await sendSubscriptionToServer(subscription.toJSON());
      return true;
    }

    // Create new subscription
    subscription = await swRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey.buffer as ArrayBuffer,
    });

    // Send to backend
    await sendSubscriptionToServer(subscription.toJSON());

    console.debug("Push subscription created successfully");
    return true;
  } catch (err) {
    console.error("Failed to subscribe to push:", err);
    return false;
  }
}

/** Unsubscribe from Web Push notifications */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!swRegistration) return false;

  try {
    const subscription = await swRegistration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
    }

    // Remove from server
    await api.delete("/users/me/push-subscription");

    console.debug("Push subscription removed");
    return true;
  } catch (err) {
    console.error("Failed to unsubscribe from push:", err);
    return false;
  }
}

/** Check if currently subscribed to push */
export async function isPushSubscribed(): Promise<boolean> {
  if (!swRegistration) return false;
  const subscription = await swRegistration.pushManager.getSubscription();
  return subscription !== null;
}

/** Send the push subscription to the backend */
async function sendSubscriptionToServer(
  subscription: PushSubscriptionJSON,
): Promise<void> {
  if (!subscription.endpoint || !subscription.keys) {
    throw new Error("Invalid push subscription — missing endpoint or keys");
  }

  await api.post("/users/me/push-subscription", {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: subscription.keys.p256dh,
      auth: subscription.keys.auth,
    },
  });
}

// ============================================================
// Browser Notifications (Fallback for non-Push)
// ============================================================

/** Request notification permission (call once on app init) */
export function requestNotificationPermission(): void {
  if (!("Notification" in window)) return;

  if (Notification.permission === "granted") {
    permissionGranted = true;
  } else if (Notification.permission !== "denied") {
    Notification.requestPermission().then((result) => {
      permissionGranted = result === "granted";
    });
  }
}

/** Show a browser notification (only when tab is not focused) */
export function showNotification(
  title: string,
  body: string,
  onClick?: () => void,
): void {
  if (!permissionGranted || document.hasFocus()) return;

  try {
    const notification = new Notification(title, {
      body,
      icon: "/favicon.svg",
      tag: "kp-message",
    });

    if (onClick) {
      notification.onclick = () => {
        window.focus();
        onClick();
        notification.close();
      };
    }

    setTimeout(() => notification.close(), 5000);
  } catch {
    // Notifications may fail in some contexts
  }
}

/** Update document title with unread count */
export function updateTitleBadge(unreadCount: number): void {
  if (unreadCount > 0) {
    document.title = `(${unreadCount}) ${DEFAULT_TITLE}`;
  } else {
    document.title = DEFAULT_TITLE;
  }
}

// ============================================================
// Utilities
// ============================================================

/** Convert a URL-safe base64 string to Uint8Array (for VAPID key) */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}
